import { ExternalLink, FileText } from 'lucide-react';
import { timeAgo } from '@reflex/ui/lib/format';
import { cn } from '@reflex/ui/lib/utils';
import {
  daemonLiveState,
  matchDaemonService,
  type DaemonLiveState,
} from '@/components/agent/agent-server-status';
import type { AgentDaemon, AgentServiceStatus } from '@/lib/types';

const LIVE_DOT_CLASS: Record<DaemonLiveState, string> = {
  running: 'bg-status-success',
  stopped: 'bg-status-error',
  unknown: 'bg-status-neutral',
};

export interface AgentServerListProps {
  /** Servers registered on the agent's sandbox (`agent.daemons`). */
  daemons: AgentDaemon[];
  /** Show a "started Xm ago" line per server (detail panel density). */
  showStarted?: boolean;
  /**
   * Live on-box service statuses (`useServiceStatuses`). When provided, each
   * server's dot reflects the real process state (pid liveness) instead of
   * the optimistic "it registered once" green, and matched servers gain a
   * logs affordance.
   */
  statuses?: AgentServiceStatus[];
  /** Open the log viewer for a server that matched a live service. */
  onViewLogs?: (daemon: AgentDaemon, service: AgentServiceStatus) => void;
  className?: string;
}

/**
 * Compact list of the servers an agent is hosting on its sandbox: a live
 * dot, the registered name, the port, the optional info line, and an
 * external link to the server's tunnel URL when one is exposed. Shared by
 * the header chip's popover and the detail panel's Servers section so both
 * surfaces render servers identically.
 */
export function AgentServerList({
  daemons,
  showStarted,
  statuses,
  onViewLogs,
  className,
}: AgentServerListProps) {
  return (
    <ul className={cn('space-y-2', className)} data-testid="agent-server-list">
      {daemons.map((daemon) => {
        const service = matchDaemonService(daemon, statuses);
        const liveState = daemonLiveState(service, statuses !== undefined);
        const liveLabel =
          liveState === 'stopped' ? 'not running' : liveState === 'unknown' ? 'status unknown' : '';
        const secondary = [daemon.info, showStarted ? `started ${timeAgo(daemon.startedAt)}` : '']
          .filter(Boolean)
          .join(' · ');
        return (
          <li
            key={`${daemon.name}:${daemon.port ?? ''}`}
            className="flex items-center gap-2.5"
            data-testid="agent-server-row"
          >
            <span
              className={cn('h-1.5 w-1.5 shrink-0 rounded-full', LIVE_DOT_CLASS[liveState])}
              title={liveLabel || 'running'}
              aria-hidden="true"
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-1.5">
                <span className="truncate text-xs font-medium text-foreground">{daemon.name}</span>
                {daemon.port != null && (
                  <span className="shrink-0 font-mono text-2xs text-muted-foreground">
                    :{daemon.port}
                  </span>
                )}
              </div>
              {(secondary || liveLabel) && (
                <p className="truncate text-2xs text-muted-foreground">
                  {liveLabel && (
                    <span
                      className={
                        liveState === 'stopped' ? 'text-status-error' : 'text-muted-foreground'
                      }
                    >
                      {liveLabel}
                    </span>
                  )}
                  {liveLabel && secondary ? ' · ' : ''}
                  {secondary}
                </p>
              )}
            </div>
            {service && onViewLogs && (
              <button
                type="button"
                onClick={() => onViewLogs(daemon, service)}
                aria-label={`View ${daemon.name} logs`}
                className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <FileText className="h-3 w-3" aria-hidden="true" />
              </button>
            )}
            {daemon.url && (
              <a
                href={daemon.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Open ${daemon.name} in a new tab`}
                className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <ExternalLink className="h-3 w-3" aria-hidden="true" />
              </a>
            )}
          </li>
        );
      })}
    </ul>
  );
}
